// custom-select.js — Huisstijl-dropdown als progressive enhancement.
//
// Vervangt de VISUELE weergave van een native <select> door een dropdown in
// de huisstijl: een gestyleerde knop plus een eigen optielijst. De native
// <select> blijft de bron van waarheid — .value, .selectedIndex en de
// change-events blijven exact werken, dus alle bestaande app-logica (die op
// die select luistert of z'n waarde uitleest) verandert niet.
//
// Robuust bij dynamiek: als de app de opties opnieuw invult of .value/
// .selectedIndex programmatisch zet, synct de knop automatisch mee
// (MutationObserver + property-interceptie).
//
// Faalt de enhancement op een veld, dan wordt dat veld teruggedraaid naar
// de gewone <select> — nooit een kapotte, onbruikbare dropdown.

const OPEN = 'cs-open';

export function enhanceAll(root) {
  const scope = root || document;
  const sels = scope.querySelectorAll('select:not([data-cs])');
  Array.prototype.forEach.call(sels, (s) => {
    try { enhanceSelect(s); } catch (e) { /* val terug op native */ }
  });
}

export function enhanceSelect(select) {
  if (!select || select.dataset.cs) return;
  select.dataset.cs = '1';

  const wrap = document.createElement('div');
  wrap.className = 'cs';
  const variant = select.getAttribute('data-cs-variant');
  if (variant) wrap.classList.add('cs--' + variant);

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'cs-button';
  btn.setAttribute('aria-haspopup', 'listbox');
  btn.setAttribute('aria-expanded', 'false');
  const aria = select.getAttribute('aria-label');
  if (aria) btn.setAttribute('aria-label', aria);

  const label = document.createElement('span');
  label.className = 'cs-label';
  btn.appendChild(label);
  btn.insertAdjacentHTML('beforeend',
    "<svg class='cs-chev' width='14' height='14' viewBox='0 0 24 24' fill='none' " +
    "stroke='currentColor' stroke-width='2.5' stroke-linecap='round' " +
    "stroke-linejoin='round' aria-hidden='true'><polyline points='6 9 12 15 18 9'/></svg>");

  const list = document.createElement('div');
  list.className = 'cs-list';
  list.setAttribute('role', 'listbox');
  list.hidden = true;

  let reverted = false;
  function revert() {
    // Zet de originele select terug alsof er niets gebeurd is.
    reverted = true;
    try {
      if (wrap.parentNode) wrap.parentNode.insertBefore(select, wrap);
      if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
    } catch (e) {}
    select.classList.remove('cs-native');
    select.removeAttribute('data-cs');
  }

  try {
    // Wrapper op de plek van de select zetten en de select erin verhuizen.
    select.parentNode.insertBefore(wrap, select);
    wrap.appendChild(select);
    wrap.appendChild(btn);
    wrap.appendChild(list);
    select.classList.add('cs-native');

    const currentOption = () => select.options[select.selectedIndex] || null;
    function rebuildLabel() {
      const o = currentOption();
      label.textContent = o ? o.textContent : '';
      const v = select.value;
      btn.classList.toggle('cs-placeholder', v === '' || v == null);
      btn.disabled = !!select.disabled;
    }
    function rebuildList() {
      list.innerHTML = '';
      Array.prototype.forEach.call(select.options, (o, i) => {
        const opt = document.createElement('div');
        opt.className = 'cs-option';
        opt.setAttribute('role', 'option');
        opt.dataset.index = String(i);
        opt.textContent = o.textContent;
        if (o.disabled) opt.classList.add('cs-disabled');
        opt.setAttribute('aria-selected', String(i === select.selectedIndex));
        // Klik (niet mousedown): bij mousedown sluit de lijst al vóór het
        // click-event, waardoor dat click-event op het element eronder belandt
        // — in een modal is dat de backdrop, die dan de popup sluit.
        // stopPropagation houdt de klik binnen de dropdown.
        opt.addEventListener('click', (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          if (o.disabled) return;
          pick(i, true);
        });
        list.appendChild(opt);
      });
    }
    function markSelected() {
      Array.prototype.forEach.call(list.children, (el) => {
        el.setAttribute('aria-selected', String(Number(el.dataset.index) === select.selectedIndex));
      });
    }
    function pick(index, fromUser) {
      select.selectedIndex = index;   // via geintercepte setter -> rebuildLabel
      rebuildLabel();
      markSelected();
      if (fromUser) {
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
        close();
        btn.focus();
      }
    }
    function open() {
      if (select.disabled || list.hidden === false) return;
      rebuildList();
      list.hidden = false;
      wrap.classList.add(OPEN);
      btn.setAttribute('aria-expanded', 'true');
      document.addEventListener('mousedown', onDocDown, true);
      document.addEventListener('keydown', onKey, true);
      // Zorg dat het geselecteerde item in beeld staat.
      const sel = list.querySelector('[aria-selected="true"]');
      if (sel && sel.scrollIntoView) sel.scrollIntoView({ block: 'nearest' });
    }
    function close() {
      if (list.hidden) return;
      list.hidden = true;
      wrap.classList.remove(OPEN);
      btn.setAttribute('aria-expanded', 'false');
      document.removeEventListener('mousedown', onDocDown, true);
      document.removeEventListener('keydown', onKey, true);
    }
    function toggle() { list.hidden ? open() : close(); }
    function onDocDown(ev) { if (!wrap.contains(ev.target)) close(); }
    function onKey(ev) {
      const opts = Array.prototype.slice.call(
        list.querySelectorAll('.cs-option:not(.cs-disabled)'));
      if (ev.key === 'Escape') { ev.preventDefault(); close(); btn.focus(); }
      else if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
        ev.preventDefault();
        let pos = opts.findIndex((o) => Number(o.dataset.index) === select.selectedIndex);
        pos = ev.key === 'ArrowDown' ? Math.min(opts.length - 1, pos + 1)
                                     : Math.max(0, pos - 1);
        const t = opts[pos];
        if (t) { pick(Number(t.dataset.index), false);
                 if (t.scrollIntoView) t.scrollIntoView({ block: 'nearest' }); }
      } else if (ev.key === 'Enter' || ev.key === ' ') {
        ev.preventDefault();
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
        close(); btn.focus();
      }
    }

    btn.addEventListener('click', (e) => { e.preventDefault(); toggle(); });
    btn.addEventListener('keydown', (e) => {
      if (list.hidden && (e.key === 'ArrowDown' || e.key === 'ArrowUp' ||
          e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); open(); }
    });

    // Sync bij dynamische opties / disabled / hidden.
    const mo = new MutationObserver(() => {
      if (reverted) return;
      rebuildLabel();
      if (!list.hidden) rebuildList();
      wrap.hidden = select.hidden;
    });
    mo.observe(select, { childList: true, attributes: true,
                         attributeFilter: ['disabled', 'hidden'] });

    // Programmatische .value / .selectedIndex onderscheppen om de knop te syncen.
    interceptProp(select, 'value', rebuildLabel);
    interceptProp(select, 'selectedIndex', rebuildLabel);
    // Extern getriggerde change -> knop bijwerken.
    select.addEventListener('change', rebuildLabel);

    rebuildLabel();
    wrap.hidden = select.hidden;
  } catch (e) {
    if (!reverted) revert();
  }
}

// Onderschept een property op een specifiek element zodat we een callback
// krijgen wanneer de app 'm zet, zonder de native werking te veranderen.
function interceptProp(el, name, cb) {
  try {
    const proto = Object.getPrototypeOf(el);
    const desc = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, name)
              || Object.getOwnPropertyDescriptor(proto, name);
    if (!desc || !desc.get || !desc.set) return;
    Object.defineProperty(el, name, {
      configurable: true,
      get() { return desc.get.call(this); },
      set(v) { desc.set.call(this, v); try { cb(); } catch (e) {} },
    });
  } catch (e) { /* interceptie optioneel */ }
}
